import { config } from "@/config";

type PrivacySection = {
  title: string;
  points: readonly string[];
};

const PRIVACY_SECTIONS: readonly PrivacySection[] = [
  {
    title: "What We Process",
    points: [
      "Uploaded fillable PDF content needed to understand and update form fields.",
      "Chat messages you send during the active session.",
      "Technical request metadata needed for reliability and abuse prevention.",
    ],
  },
  {
    title: "How Session Handling Works",
    points: [
      "PDFPilot is designed around short-lived sessions, not long-term user accounts.",
      "You control when to upload, continue, and close a session.",
      "Server-side persistence after request processing is intentionally minimized for MVP scope.",
    ],
  },
  {
    title: "Security Practices",
    points: [
      "Transport is expected to run over HTTPS in deployed environments.",
      "The app applies verification and session controls to reduce abusive traffic.",
      "Access to internal AI instructions is restricted by assistant guardrails.",
    ],
  },
  {
    title: "Product Limits You Should Know",
    points: [
      "The MVP supports one AcroForm PDF at a time.",
      "Complex field types and non-AcroForm scans are out of scope.",
      "Always review your final exported PDF before submitting it to any authority.",
    ],
  },
];

const PRIVACY_LAST_UPDATED: string = "February 19, 2026";

const PRIVACY_BACKGROUND_STYLE: React.CSSProperties = {
  backgroundImage:
    "radial-gradient(circle at 0% 0%, color-mix(in oklab, var(--color-primary) 16%, transparent) 0%, transparent 34%), radial-gradient(circle at 100% 20%, color-mix(in oklab, var(--color-accent) 28%, transparent) 0%, transparent 36%)",
};

const SECTION_CARD_CLASS_NAME = "rounded-2xl border border-border/70 bg-card/75 p-5 sm:p-6";

type PrivacySectionCardProps = {
  section: PrivacySection;
};

function PrivacySectionCard({ section }: PrivacySectionCardProps): React.JSX.Element {
  return (
    <article className={SECTION_CARD_CLASS_NAME}>
      <h2 className="font-serif text-2xl font-bold tracking-tight">{section.title}</h2>
      <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
        {section.points.map((point: string) => (
          <li key={point}>- {point}</li>
        ))}
      </ul>
    </article>
  );
}

export function PrivacyPage(): React.JSX.Element {
  return (
    <main className="relative min-h-screen overflow-x-hidden bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0" style={PRIVACY_BACKGROUND_STYLE} />

      <div className="relative z-10 mx-auto max-w-4xl px-5 py-8 sm:px-8 sm:py-12">
        <header className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/70 bg-card/75 px-4 py-3">
          <a
            href="/"
            className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase transition-colors hover:text-foreground"
          >
            Back to landing
          </a>
          <a
            href={config.appBaseUrl}
            className="rounded-full bg-primary px-4 py-2 text-xs font-semibold tracking-[0.14em] text-primary-foreground uppercase transition-opacity hover:opacity-90"
          >
            Open App
          </a>
        </header>

        <section className="mt-8 rounded-3xl border border-border/70 bg-card/80 p-6 sm:p-8">
          <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">PDFPilot</p>
          <h1 className="mt-2 font-serif text-4xl leading-tight font-bold tracking-tight text-foreground sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
            This page explains how PDFPilot handles data for the MVP release. Our goal is to keep
            processing clear, narrow, and practical for form completion workflows.
          </p>
          <p className="mt-3 text-xs tracking-[0.14em] text-muted-foreground uppercase">
            Last updated: {PRIVACY_LAST_UPDATED}
          </p>
        </section>

        <section className="mt-6 space-y-4">
          {PRIVACY_SECTIONS.map((section: PrivacySection) => (
            <PrivacySectionCard key={section.title} section={section} />
          ))}
        </section>

        <section className={`mt-6 ${SECTION_CARD_CLASS_NAME}`}>
          <h2 className="font-serif text-2xl font-bold tracking-tight">Questions</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
            If you have privacy questions about the product behavior in your environment, contact
            the PDFPilot team through your project support channel before processing sensitive
            records.
          </p>
        </section>
      </div>
    </main>
  );
}
